// ═══════════════════════════════════════════════════════════════════════
// Reports.js — CONSOLIDATED REPORTS with Past Maturity & Overdue
// ═══════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';

// ═══════════════════════════════════════════════════════════════════════
// LOAN STATUS COLOR MAP
// ═══════════════════════════════════════════════════════════════════════
export const LOAN_STATUS_STYLES = {
  active:        { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: '🟢', label: 'Active' },
  overdue:       { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: '🔴', label: 'Overdue' },
  defaulted:     { color: '#7f1d1d', bg: '#450a0a', border: '#7f1d1d', icon: '⚫', label: 'Defaulted', lightBg: '#fef2f2', lightBorder: '#fca5a5' },
  closed:        { color: '#64748b', bg: '#f8fafc', border: '#e2e8f0', icon: '⚪', label: 'Closed' },
  'written-off': { color: '#1e293b', bg: '#f1f5f9', border: '#cbd5e1', icon: '🚫', label: 'Written Off' },
  disbursing:    { color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: '🟡', label: 'Disbursing' },
  pending:       { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: '🔵', label: 'Pending' },
  approved:      { color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: '✅', label: 'Approved' },
  rejected:      { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: '❌', label: 'Rejected' },
  submitted:     { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: '📤', label: 'Submitted' },
};

// ═══════════════════════════════════════════════════════════════════════
// ACHIEVEMENT COLOR HELPER
// ═══════════════════════════════════════════════════════════════════════
const getAchievementColor = (rate) => {
  const r = parseFloat(rate) || 0;
  if (r >= 100) return '#10b981';
  if (r >= 80) return '#84cc16';
  if (r >= 60) return '#eab308';
  if (r >= 40) return '#f97316';
  if (r >= 20) return '#ef4444';
  return '#dc2626';
};

// ═══════════════════════════════════════════════════════════════════════
// CONSOLIDATED REPORT CATEGORIES (7 categories, ~27 reports)
// ═══════════════════════════════════════════════════════════════════════
const REPORT_CATEGORIES = [
  {
    title: 'Performance Dashboards', icon: '🏆',
    reports: [
      { id: 'dash-leads', name: 'Leads Update', desc: 'Branch leads target vs actual with color coding', endpoint: '/reports/branch-leads', type: 'branch-performance', filters: ['branch', 'dateRange'] },
      { id: 'dash-daily-loans', name: 'Daily New Loans Performance', desc: 'Day-by-day disbursement with cumulative totals', endpoint: '/reports/daily-loans-performance', type: 'branch-performance', filters: ['branch', 'dateRange', 'groupByBranch'] },
      { id: 'dash-eom-collections', name: 'Daily Collections Report', desc: 'Day-by-day collections with cumulative totals', endpoint: '/reports/eom-collections', type: 'branch-performance', filters: ['branch', 'dateRange', 'groupByBranch'] },
      { id: 'monthly-collection-rate', name: 'Monthly Collection Rate', desc: 'Loans are credited to their disbursement month: total payable (principal + interest + penalties) versus all repayments received', endpoint: '/reports/monthly-collection-rate', type: 'collection-rate', filters: ['branch', 'dateRange'] },
      { id: 'dash-onboarding-bonus', name: 'Onboarding Bonus Results', desc: 'LO/CO performance by branch', endpoint: '/reports/onboarding-bonus', type: 'branch-performance', filters: ['branch', 'dateRange'] },
      { id: 'dash-branch-summary', name: 'Branch Disb. & Collection Summary', desc: 'Daily combined disbursement and collections', endpoint: '/reports/branch-summary', type: 'branch-performance', filters: ['branch', 'dateRange'] },
    ]
  },
  {
    title: 'Loan Portfolio', icon: '📋',
    reports: [
      { id: 'loans-general', name: 'Loans General Report', desc: 'All active loans with full details', endpoint: '/reports/detailed-loans', type: 'loans', filters: ['branch', 'status', 'search', 'dateRange'] },
      { id: 'portfolio-overview', name: 'Portfolio Overview', desc: 'Complete portfolio snapshot with all statuses', endpoint: '/reports/detailed-loans', type: 'loans', filters: ['branch', 'search', 'dateRange'] },
      { id: 'past-maturity-overdue', name: 'Past Maturity & Overdue Loans', desc: 'Loans past maturity date with aging analysis', endpoint: '/reports/past-maturity-overdue', type: 'past-maturity-overdue', filters: ['branch', 'search'] },
      { id: 'aging-analysis', name: 'Loan Aging Analysis', desc: 'Loans grouped by days overdue', endpoint: '/reports/detailed-loans', type: 'loans', filters: ['branch', 'search', 'dateRange'], defaultStatus: 'overdue' },
      { id: 'defaulted-loans', name: 'Defaulted Loans', desc: 'All defaulted loans with amounts', endpoint: '/reports/detailed-loans', type: 'loans', filters: ['branch', 'search', 'dateRange'], defaultStatus: 'defaulted' },
    ]
  },
  {
    title: 'Disbursements & Collections', icon: '💰',
    reports: [
      { id: 'disbursements', name: 'Disbursements Report', desc: 'All loan disbursements within a period', endpoint: '/reports/detailed-loans', type: 'loans', filters: ['branch', 'dateRange'] },
      { id: 'collections', name: 'Collections Report', desc: 'All collection transactions with breakdown', endpoint: '/reports/detailed-repayments', type: 'repayments', filters: ['branch', 'dateRange', 'paymentSource'] },
      { id: 'transactions-merged', name: 'All Transactions (Merged)', desc: 'Combined disbursements and repayments', endpoint: '/reports/detailed-repayments', type: 'repayments', filters: ['branch', 'dateRange', 'paymentSource'] },
    ]
  },
  {
    title: 'Closed & Written Off', icon: '🔒',
    reports: [
      { id: 'closed-loans', name: 'Closed Loans', desc: 'All closed and written-off loans', endpoint: '/loans/closed', type: 'closed-loans', filters: ['branch', 'search', 'closedDateRange', 'closedStatus'] },
      { id: 'written-off', name: 'Written Off Loans', desc: 'Complete list of written-off loans', endpoint: '/loans/closed', type: 'closed-loans', filters: ['branch', 'search', 'closedDateRange'], defaultClosedStatus: 'written-off' },
      { id: 'write-off-recovery', name: 'Write-Off Recovery', desc: 'Collections on previously written-off loans', endpoint: '/reports/detailed-repayments', type: 'repayments', filters: ['branch', 'dateRange', 'paymentSource'] },
    ]
  },
  {
    title: 'Customers & Leads', icon: '👥',
    reports: [
      { id: 'customers', name: 'Customer Report', desc: 'All customers with status and details', endpoint: '/clients', type: 'clients', filters: ['branch', 'status', 'search'] },
      { id: 'new-customers', name: 'New Customers Report', desc: 'Clients created within the selected date range, with new-client tagging and branch details', endpoint: '/clients', type: 'clients', filters: ['branch', 'search', 'dateRange'] },
      { id: 'dormant-clients', name: 'Dormant Clients Report', desc: 'Clients currently marked as dormant, with branch, loan and activity indicators', endpoint: '/clients', type: 'clients', filters: ['branch', 'search'], defaultClientStatus: 'dormant' },
      { id: 'customers-active-loans', name: 'Customers with Active Loans', desc: 'Customers who currently have active loans', endpoint: '/reports/detailed-loans', type: 'loans', filters: ['branch', 'search'], defaultStatus: 'active' },
      { id: 'leads-pipeline', name: 'Lead Pipeline', desc: 'All leads by status in the pipeline', endpoint: '/leads', type: 'leads', filters: ['branch', 'leadStatus', 'search', 'dateRange'] },
      { id: 'leads-converted', name: 'Lead Conversions', desc: 'Leads that converted to clients', endpoint: '/leads', type: 'leads', filters: ['branch', 'search', 'dateRange'], defaultLeadStatus: 'converted' },
    ]
  },
  {
    title: 'Financial & Performance', icon: '📈',
    reports: [
      { id: 'financial-summary', name: 'Financial Summary', desc: 'All repayments with full breakdown', endpoint: '/reports/detailed-repayments', type: 'repayments', filters: ['branch', 'dateRange', 'paymentSource'] },
      { id: 'interest-earned', name: 'Interest Earned', desc: 'Total interest collected on all loans', endpoint: '/reports/detailed-repayments', type: 'repayments', filters: ['branch', 'dateRange'] },
      { id: 'fees-charges', name: 'Fees & Charges', desc: 'Processing, insurance and penalty fees', endpoint: '/reports/detailed-loans', type: 'loans', filters: ['branch', 'dateRange'] },
      { id: 'officer-performance', name: 'Officer Performance', desc: 'Loan/collection officer metrics', endpoint: '/reports/officer-performance', type: 'officer', filters: ['branch', 'officerType', 'dateRange'] },
      { id: 'officer-interaction-workload', name: 'Interactions Report', desc: 'Officer interaction totals by leads, clients, active-loan and overdue-loan follow-ups', endpoint: '/reports/officer-interaction-workload', type: 'officer-workload', filters: ['branch', 'officerType', 'dateRange'] },
      { id: 'branch-performance', name: 'Branch Performance', desc: 'Disbursement and collection by branch', endpoint: '/reports/officer-performance', type: 'officer', filters: ['branch', 'dateRange'] },
    ]
  },
  {
    title: 'Applications & Audit', icon: '🔍',
    reports: [
      { id: 'applications', name: 'Loan Applications', desc: 'All applications with status tracking', endpoint: '/loan-applications', type: 'applications', filters: ['branch', 'search', 'dateRange', 'appStatus'] },
      { id: 'audit-trail', name: 'Audit Trail', desc: 'Complete system action history', endpoint: '/audit-logs', type: 'audit', filters: ['module', 'action', 'search', 'dateRange'] },
    ]
  },
];

// ═══════════════════════════════════════════════════════════════════════
// FILTER OPTIONS
// ═══════════════════════════════════════════════════════════════════════
const LOAN_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' }, { value: 'active', label: 'Active' }, { value: 'overdue', label: 'Overdue' },
  { value: 'defaulted', label: 'Defaulted' }, { value: 'closed', label: 'Closed' }, { value: 'written-off', label: 'Written Off' },
];
const CLOSED_STATUS_OPTIONS = [{ value: '', label: 'All Closed/Written-Off' }, { value: 'closed', label: 'Closed Only' }, { value: 'written-off', label: 'Written Off Only' }];
const PAYMENT_SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' }, { value: 'c2b-auto', label: 'M-Pesa Auto' }, { value: 'counter', label: 'Counter' },
  { value: 'manual', label: 'Manual' }, { value: 'bank-transfer', label: 'Bank Transfer' },
];
const MODULE_OPTIONS = [
  { value: '', label: 'All Modules' }, { value: 'loans', label: 'Loans' }, { value: 'clients', label: 'Clients' },
  { value: 'repayments', label: 'Repayments' }, { value: 'users', label: 'Users' }, { value: 'loan-applications', label: 'Applications' },
];
const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' }, { value: 'CREATE', label: 'Create' }, { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' }, { value: 'APPROVE', label: 'Approve' }, { value: 'DISBURSE', label: 'Disburse' },
  { value: 'CLOSE', label: 'Close' }, { value: 'WRITE_OFF', label: 'Write Off' },
];
const OFFICER_TYPE_OPTIONS = [{ value: 'all', label: 'All Officers' }, { value: 'loan-officer', label: 'Loan Officers' }, { value: 'collection-officer', label: 'Collection Officers' }];
const LEAD_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' }, { value: 'new', label: 'New' }, { value: 'contacted', label: 'Contacted' },
  { value: 'interested', label: 'Interested' }, { value: 'converted', label: 'Converted' }, { value: 'lost', label: 'Lost' },
];
const APP_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' }, { value: 'draft', label: 'Draft' }, { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }, { value: 'disbursed', label: 'Disbursed' },
];

// ═══════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════
const S = {
  page: { padding: '20px 24px', maxWidth: 1600, margin: '0 auto' },
  catCard: { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  catHeader: { padding: '14px 20px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10 },
  catTitle: { fontSize: 15, fontWeight: 700, color: '#1e293b', margin: 0 },
  catCount: { marginLeft: 'auto', fontSize: 12, color: '#64748b', background: '#f1f5f9', padding: '2px 10px', borderRadius: 12, fontWeight: 600 },
  reportRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.15s' },
  reportName: { fontSize: 14, fontWeight: 500, color: '#334155' },
  reportDesc: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  viewBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 18px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  filterBar: { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', padding: 16, background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 16 },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: 3 },
  filterLabel: { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em' },
  filterSelect: { padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, minWidth: 150, background: '#fff', outline: 'none' },
  filterInput: { padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, minWidth: 180, background: '#fff', outline: 'none' },
  filterDate: { padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, width: 160, background: '#fff', outline: 'none' },
  btnPrimary: { padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  btnExport: { padding: '8px 20px', borderRadius: 8, border: '1px solid #059669', background: '#ecfdf5', color: '#059669', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 },
  btnExportDisabled: { padding: '8px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: '#f1f5f9', color: '#94a3b8', cursor: 'not-allowed', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 },
  summaryBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginBottom: 16 },
  summaryCard: { padding: '14px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff' },
  summaryLabel: { fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 },
  summaryValue: { fontSize: 20, fontWeight: 700, color: '#1e293b' },
  tableWrap: { overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '10px 14px', textAlign: 'left', background: '#f1f5f9', borderBottom: '2px solid #e2e8f0', fontWeight: 600, color: '#475569', fontSize: 12, textTransform: 'uppercase', whiteSpace: 'nowrap' },
  thRight: { padding: '10px 14px', textAlign: 'right', background: '#f1f5f9', borderBottom: '2px solid #e2e8f0', fontWeight: 600, color: '#475569', fontSize: 12, textTransform: 'uppercase', whiteSpace: 'nowrap' },
  td: { padding: '10px 14px', borderBottom: '1px solid #f1f5f9', color: '#334155' },
  tdRight: { padding: '10px 14px', borderBottom: '1px solid #f1f5f9', color: '#334155', textAlign: 'right', fontFamily: 'monospace' },
  tdMono: { padding: '10px 14px', borderBottom: '1px solid #f1f5f9', color: '#334155', fontFamily: 'monospace', fontWeight: 600 },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #e5e7eb', fontSize: 13, color: '#64748b' },
  pageBtn: { padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12 },
  pageBtnActive: { padding: '5px 10px', border: '1px solid #2563eb', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  pageBtnDisabled: { padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f1f5f9', color: '#94a3b8', cursor: 'not-allowed', fontSize: 12 },
  emptyState: { padding: '60px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 14 },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: '#64748b', fontSize: 14, gap: 8 },
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 16 },
  reportTitle: { fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 },
  reportSubtitle: { fontSize: 13, color: '#64748b', margin: '4px 0 0 0' },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#dc2626', fontSize: 13 },
  infoBox: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#2563eb', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 },
  perfCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 20, boxShadow: '0 2px 4px rgba(0,0,0,0.03)' },
  perfHeader: { background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', color: '#fff', padding: '12px 20px', fontSize: 15, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  perfTh: { padding: '10px 12px', background: '#f8fafc', color: '#475569', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', textAlign: 'left', whiteSpace: 'nowrap' },
  perfThRight: { padding: '10px 12px', background: '#f8fafc', color: '#475569', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', textAlign: 'right', whiteSpace: 'nowrap' },
  perfTd: { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 12, color: '#334155', whiteSpace: 'nowrap' },
  perfTdRight: { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 12, color: '#334155', textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap' },
  perfTotalsRow: { background: '#f1f5f9', fontWeight: 800, borderTop: '2px solid #cbd5e1' },
  rateBadge: { display: 'inline-block', padding: '4px 10px', borderRadius: 6, fontWeight: 700, fontSize: 12, minWidth: 56, textAlign: 'center' },
  agingGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginBottom: 20 },
  agingCard: { padding: '14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', textAlign: 'center' },
  agingLabel: { fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 },
  agingCount: { fontSize: 22, fontWeight: 800, color: '#1e293b' },
  agingAmount: { fontSize: 12, color: '#64748b', marginTop: 2 },
  twoColGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 },
  statusBreakdownRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #f1f5f9' },
  checkboxGroup: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, paddingTop: 18 },
  checkboxLabel: { fontSize: 13, fontWeight: 600, color: '#334155', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' },
  exportSpinner: { display: 'inline-block', width: 14, height: 14, border: '2px solid #059669', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' },
};

// Add spinner keyframes to document
if (typeof document !== 'undefined' && !document.getElementById('export-spinner-style')) {
  const style = document.createElement('style');
  style.id = 'export-spinner-style';
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

// ═══════════════════════════════════════════════════════════════════════
// COLUMN DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════
const statusBadge = (s) => {
  const st = LOAN_STATUS_STYLES[s] || LOAN_STATUS_STYLES.active;
  const bg = st.lightBg || st.bg;
  const border = st.lightBorder || st.border;
  return { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: bg, color: st.color, border: '1px solid ' + border, textTransform: 'uppercase' };
};

const LOAN_COLUMNS = [
  { key: 'loanNumber', label: 'Loan No', render: (v) => <span style={S.tdMono}>{v}</span> },
  { key: 'clientName', label: 'Client', render: (v) => v || 'N/A' },
  { key: 'clientNumber', label: 'Client ID', render: (v) => <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>{v || 'N/A'}</span> },
  { key: 'clientPhone', label: 'Phone', render: (v) => v || '—' },
  { key: 'product', label: 'Product', render: (v) => v || 'N/A' },
  { key: 'principalAmount', label: 'Principal', align: 'right', render: (v) => formatCurrency(v) },
  { key: 'disbursementAmount', label: 'Net Disbursed', align: 'right', render: (v) => formatCurrency(v) },
  { key: 'disbursementDate', label: 'Disb. Date', render: (v) => v ? formatDate(v) : '—' },
  { key: 'outstandingPrincipal', label: 'Outstanding P', align: 'right', render: (v) => <span style={{ color: '#dc2626' }}>{formatCurrency(v)}</span> },
  { key: 'outstandingInterest', label: 'Outstanding I', align: 'right', render: (v) => <span style={{ color: '#d97706' }}>{formatCurrency(v)}</span> },
  { key: 'outstandingPenalty', label: 'Penalty', align: 'right', render: (v) => <span style={{ color: v > 0 ? '#dc2626' : '#94a3b8' }}>{formatCurrency(v)}</span> },
  { key: 'totalOutstanding', label: 'Total Outstanding', align: 'right', render: (v) => <span style={{ fontWeight: 700, color: v > 0 ? '#dc2626' : '#059669' }}>{formatCurrency(v)}</span> },
  { key: 'totalPaid', label: 'Total Paid', align: 'right', render: (v) => <span style={{ color: '#059669' }}>{formatCurrency(v)}</span> },
  { key: 'status', label: 'Status', render: (v) => { const st = LOAN_STATUS_STYLES[v] || LOAN_STATUS_STYLES.active; return <span style={statusBadge(v)}>{st.icon} {st.label}</span>; } },
  { key: 'daysOverdue', label: 'Days Overdue', align: 'right', render: (v) => <span style={{ color: v > 0 ? '#dc2626' : '#059669', fontWeight: v > 0 ? 700 : 400 }}>{v || 0}</span> },
  { key: 'branchName', label: 'Branch', render: (v) => v || '—' },
  { key: 'loanOfficer', label: 'Loan Officer', render: (v) => v || '—' },
];

const PAST_MATURITY_COLUMNS = [
  { key: 'loanNumber', label: 'Loan No', render: (v) => <span style={S.tdMono}>{v}</span> },
  { key: 'clientName', label: 'Client', render: (v) => v || 'N/A' },
  { key: 'clientPhone', label: 'Phone', render: (v) => v || '—' },
  { key: 'branchName', label: 'Branch', render: (v) => v || '—' },
  { key: 'officerName', label: 'Loan Officer', render: (v) => v || '—' },
  { key: 'principalAmount', label: 'Principal', align: 'right', render: (v) => formatCurrency(v) },
  { key: 'maturityDate', label: 'Maturity Date', render: (v) => v ? <span style={{ fontWeight: 600, color: '#dc2626' }}>{formatDate(v)}</span> : '—' },
  { key: 'daysPastMaturity', label: 'Days Past', align: 'right', render: (v) => {
    const d = v || 0;
    let color = '#dc2626';
    if (d <= 7) color = '#d97706';
    if (d <= 1) color = '#eab308';
    return <span style={{ color, fontWeight: 700, fontSize: 14 }}>{d}</span>;
  }},
  { key: 'totalOutstanding', label: 'Total Outstanding', align: 'right', render: (v) => <span style={{ fontWeight: 700, color: '#dc2626' }}>{formatCurrency(v)}</span> },
  { key: 'outstandingPrincipal', label: 'Outstanding P', align: 'right', render: (v) => formatCurrency(v) },
  { key: 'outstandingInterest', label: 'Outstanding I', align: 'right', render: (v) => <span style={{ color: '#d97706' }}>{formatCurrency(v)}</span> },
  { key: 'outstandingPenalty', label: 'Penalty', align: 'right', render: (v) => <span style={{ color: v > 0 ? '#dc2626' : '#94a3b8' }}>{formatCurrency(v)}</span> },
  { key: 'totalPaid', label: 'Total Paid', align: 'right', render: (v) => <span style={{ color: '#059669' }}>{formatCurrency(v)}</span> },
  { key: 'status', label: 'Status', render: (v) => { const st = LOAN_STATUS_STYLES[v] || LOAN_STATUS_STYLES.active; return <span style={statusBadge(v)}>{st.icon} {st.label}</span>; } },
];

const CLOSED_LOAN_COLUMNS = [
  { key: 'loanNumber', label: 'Loan No', render: (v) => <span style={S.tdMono}>{v}</span> },
  { key: 'clientName', label: 'Client', render: (v) => v || 'N/A' },
  { key: 'clientNumber', label: 'Client ID', render: (v) => <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>{v || '—'}</span> },
  { key: 'clientPhone', label: 'Phone', render: (v) => v || '—' },
  { key: 'branchName', label: 'Branch', render: (v) => v || '—' },
  { key: 'principalAmount', label: 'Principal', align: 'right', render: (v) => formatCurrency(v) },
  { key: 'disbursementAmount', label: 'Net Disbursed', align: 'right', render: (v) => formatCurrency(v) },
  { key: 'totalInterest', label: 'Total Interest', align: 'right', render: (v) => <span style={{ color: '#d97706' }}>{formatCurrency(v || 0)}</span> },
  { key: 'totalPaid', label: 'Total Paid', align: 'right', render: (v) => <span style={{ color: '#059669', fontWeight: 600 }}>{formatCurrency(v || 0)}</span> },
  { key: 'principalPaid', label: 'Principal Paid', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'interestPaid', label: 'Interest Paid', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'totalOutstanding', label: 'Outstanding', align: 'right', render: (v) => <span style={{ color: (v || 0) <= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>{formatCurrency(v || 0)}</span> },
  { key: 'actualDaysUsed', label: 'Days Used', align: 'right', render: (v) => <span style={{ fontWeight: 600 }}>{v || 0}d</span> },
  { key: 'finalInterestRate', label: 'Final Rate', align: 'right', render: (v) => <span style={{ color: '#d97706', fontWeight: 600 }}>{v ? `${v}%` : '—'}</span> },
  { key: 'disbursementDate', label: 'Disb. Date', render: (v) => v ? formatDate(v) : '—' },
  { key: 'closedAt', label: 'Closed Date', render: (v) => v ? <span style={{ fontWeight: 600 }}>{formatDate(v)}</span> : '—' },
  { key: 'closedByName', label: 'Closed By', render: (v) => v || '—' },
  { key: 'closureReason', label: 'Reason', render: (v) => v ? <span style={{ maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v}>{v}</span> : <span style={{ color: '#94a3b8' }}>—</span> },
  { key: 'status', label: 'Status', render: (v) => { const st = LOAN_STATUS_STYLES[v] || LOAN_STATUS_STYLES.closed; return <span style={statusBadge(v)}>{st.icon} {st.label}</span>; } },
];

const REPAYMENT_COLUMNS = [
  { key: 'receiptNumber', label: 'Receipt', render: (v) => <span style={S.tdMono}>{v || '—'}</span> },
  { key: 'createdAt', label: 'Date/Time', render: (v) => v ? <span style={{ whiteSpace: 'nowrap' }}>{new Date(v).toLocaleDateString()} {new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span> : '—' },
  { key: 'loanNumber', label: 'Loan No', render: (v) => <span style={S.tdMono}>{v || '—'}</span> },
  { key: 'clientName', label: 'Client', render: (v) => v || 'N/A' },
  { key: 'clientNumber', label: 'Client ID', render: (v) => <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'monospace' }}>{v || '—'}</span> },
  { key: 'clientPhone', label: 'Phone', render: (v) => v || '—' },
  { key: 'amount', label: 'Total Amount', align: 'right', render: (v) => <span style={{ fontWeight: 700, color: '#059669' }}>{formatCurrency(v)}</span> },
  { key: 'principalAmount', label: 'Principal', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'interestAmount', label: 'Interest', align: 'right', render: (v) => <span style={{ color: '#d97706' }}>{formatCurrency(v || 0)}</span> },
  { key: 'penaltyAmount', label: 'Penalty', align: 'right', render: (v) => <span style={{ color: v > 0 ? '#dc2626' : '#94a3b8' }}>{formatCurrency(v || 0)}</span> },
  { key: 'paymentSource', label: 'Source', render: (v) => {
    const map = { 'c2b-auto': { l: 'M-Pesa', c: '#059669', bg: '#ecfdf5' }, 'counter': { l: 'Counter', c: '#2563eb', bg: '#eff6ff' }, 'manual': { l: 'Manual', c: '#d97706', bg: '#fffbeb' }, 'bank-transfer': { l: 'Bank', c: '#7c3aed', bg: '#f5f3ff' } };
    const s = map[v] || { l: v || 'N/A', c: '#64748b', bg: '#f1f5f9' };
    return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600, color: s.c, background: s.bg }}>{s.l}</span>;
  }},
  { key: 'paymentMethod', label: 'Method', render: (v) => (v || '—').replace(/-/g, ' ').toUpperCase() },
  { key: 'paymentReference', label: 'Reference', render: (v) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v || '—'}</span> },
  { key: 'collectedBy', label: 'Collected By', render: (v) => v || <span style={{ color: '#059669', fontWeight: 600, fontSize: 11 }}>SYSTEM</span> },
  { key: 'branchName', label: 'Branch', render: (v) => v || '—' },
  { key: 'loanStatus', label: 'Loan Status', render: (v) => { if (!v) return '—'; var st = LOAN_STATUS_STYLES[v] || LOAN_STATUS_STYLES.active; return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600, background: st.bg, color: st.color }}>{st.label}</span>; } },
];

const LEAD_COLUMNS = [
  { key: 'firstName', label: 'First Name', render: (v) => v || '—' },
  { key: 'lastName', label: 'Last Name', render: (v) => v || '—' },
  { key: 'phone', label: 'Phone', render: (v) => v || '—' },
  { key: 'source', label: 'Source', render: (v) => <span style={{ textTransform: 'capitalize' }}>{(v || '—').replace(/-/g, ' ')}</span> },
  { key: 'status', label: 'Status', render: (v) => {
    const map = { 'new': { c: '#2563eb', bg: '#eff6ff' }, 'contacted': { c: '#7c3aed', bg: '#f5f3ff' }, 'interested': { c: '#059669', bg: '#ecfdf5' }, 'converted': { c: '#059669', bg: '#ecfdf5' }, 'lost': { c: '#dc2626', bg: '#fef2f2' } };
    const s = map[v] || { c: '#64748b', bg: '#f1f5f9' };
    return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600, color: s.c, background: s.bg, textTransform: 'capitalize' }}>{v}</span>;
  }},
  { key: 'assignedTo', label: 'Assigned To', render: (v) => v ? `${v.firstName || ''} ${v.lastName || ''}` : <span style={{ color: '#94a3b8' }}>Unassigned</span> },
  { key: 'createdAt', label: 'Created', render: (v) => v ? formatDate(v) : '—' },
  { key: 'followUpDate', label: 'Follow-up', render: (v) => { if (!v) return <span style={{ color: '#94a3b8' }}>—</span>; const isPast = new Date(v) < new Date(); return <span style={{ color: isPast ? '#dc2626' : '#2563eb' }}>{formatDate(v)}</span>; } },
];

const APPLICATION_COLUMNS = [
  { key: 'applicationNumber', label: 'App. No', render: (v) => <span style={S.tdMono}>{v || '—'}</span> },
  { key: 'clientName', label: 'Client', render: (v, r) => r.client ? `${r.client.firstName || ''} ${r.client.lastName || ''}` : '—' },
  { key: 'loanProduct', label: 'Product', render: (v, r) => r.loanProduct?.name || v || '—' },
  { key: 'requestedAmount', label: 'Requested', align: 'right', render: (v) => formatCurrency(v) },
  { key: 'approvedAmount', label: 'Approved', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'status', label: 'Status', render: (v) => { const st = LOAN_STATUS_STYLES[v] || LOAN_STATUS_STYLES.pending; return <span style={statusBadge(v)}>{st.icon} {st.label}</span>; } },
  { key: 'assignedTo', label: 'Assigned To', render: (v) => v ? `${v.firstName || ''} ${v.lastName || ''}` : <span style={{ color: '#94a3b8' }}>—</span> },
  { key: 'createdAt', label: 'Created', render: (v) => v ? formatDate(v) : '—' },
  { key: 'submittedAt', label: 'Submitted', render: (v) => v ? formatDate(v) : '—' },
  { key: 'approvedAt', label: 'Approved', render: (v) => v ? formatDate(v) : '—' },
];

const CLIENT_COLUMNS = [
  { key: 'clientNumber', label: 'Client ID', render: (v) => <span style={S.tdMono}>{v || 'N/A'}</span> },
  { key: 'name', label: 'Name', render: (v, r) => `${r.firstName || ''} ${r.lastName || ''}` },
  { key: 'phone', label: 'Phone', render: (v) => v || '—' },
  { key: 'email', label: 'Email', render: (v) => v || '—' },
  { key: 'status', label: 'Status', render: (v) => {
    const map = { 'approved': { c: '#059669', bg: '#ecfdf5' }, 'pending-approval': { c: '#d97706', bg: '#fffbeb' }, 'rejected': { c: '#dc2626', bg: '#fef2f2' }, 'blacklisted': { c: '#991b1b', bg: '#fef2f2' }, 'dormant': { c: '#7c3aed', bg: '#f5f3ff' } };
    const s = map[v] || { c: '#64748b', bg: '#f1f5f9' };
    return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600, color: s.c, background: s.bg, textTransform: 'capitalize' }}>{v}</span>;
  }},
  { key: 'isNewClient', label: 'New', render: (v) => v ? <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 800, color: '#1d4ed8', background: '#eff6ff' }}>NEW</span> : '—' },
  { key: 'activeLoans', label: 'Active Loans', align: 'right', render: (v, r) => v ?? r.computedActiveLoans ?? 0 },
  { key: 'branch', label: 'Branch', render: (v) => (v && v.name) ? v.name : (v || '—') },
  { key: 'createdAt', label: 'Created', render: (v) => v ? formatDate(v) : '—' },
];

const AUDIT_COLUMNS = [
  { key: 'createdAt', label: 'Date', render: (v) => formatDate(v) },
  { key: 'userName', label: 'User', render: (v) => v || 'System' },
  { key: 'userRole', label: 'Role', render: (v) => <span style={{ fontSize: 11, color: '#64748b' }}>{v || '—'}</span> },
  { key: 'action', label: 'Action', render: (v) => {
    const map = { CREATE: { c: '#059669', bg: '#ecfdf5' }, UPDATE: { c: '#2563eb', bg: '#eff6ff' }, DELETE: { c: '#dc2626', bg: '#fef2f2' }, APPROVE: { c: '#059669', bg: '#ecfdf5' }, DISBURSE: { c: '#7c3aed', bg: '#f5f3ff' }, CLOSE: { c: '#64748b', bg: '#f1f5f9' }, WRITE_OFF: { c: '#1e293b', bg: '#f1f5f9' } };
    const s = map[v] || { c: '#64748b', bg: '#f1f5f9' };
    return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 700, color: s.c, background: s.bg }}>{v}</span>;
  }},
  { key: 'module', label: 'Module', render: (v) => <span style={{ textTransform: 'capitalize' }}>{v || '—'}</span> },
  { key: 'description', label: 'Description', render: (v) => <span style={{ maxWidth: 400, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v}>{v || '—'}</span> },
  { key: 'ipAddress', label: 'IP Address', render: (v) => <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#94a3b8' }}>{v || '—'}</span> },
];

const OFFICER_COLUMNS = [
  { key: 'officerName', label: 'Officer', render: (v) => v || '—' },
  { key: 'officerType', label: 'Type', render: (v) => <span style={{ textTransform: 'uppercase', fontSize: 11, fontWeight: 600 }}>{v || '—'}</span> },
  { key: 'branchName', label: 'Branch', render: (v) => v || '—' },
  { key: 'disbursementCount', label: 'Disbursed', align: 'right', render: (v) => v || 0 },
  { key: 'disbursementAmount', label: 'Disb. Amount', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'collectionCount', label: 'Collections', align: 'right', render: (v) => v || 0 },
  { key: 'collectionAmount', label: 'Coll. Amount', align: 'right', render: (v) => formatCurrency(v || 0) },
];


const OFFICER_WORKLOAD_COLUMNS = [
  { key: 'officerName', label: 'Officer' },
  { key: 'officerType', label: 'Role', render: (v) => <span style={{ textTransform: 'capitalize', fontSize: 11, fontWeight: 700 }}>{String(v || '—').replace(/-/g, ' ')}</span> },
  { key: 'branchName', label: 'Branch' },
  { key: 'totalInteractions', label: 'Total Interactions', align: 'right', render: (v) => <strong>{v || 0}</strong> },
  { key: 'leadInteractions', label: 'Leads', align: 'right', render: (v) => v || 0 },
  { key: 'clientInteractions', label: 'Clients', align: 'right', render: (v) => v || 0 },
  { key: 'activeLoanInteractions', label: 'Active Loans', align: 'right', render: (v) => v || 0 },
  { key: 'overdueLoanInteractions', label: 'Overdue Loans', align: 'right', render: (v) => <span style={{ fontWeight: 700, color: (v || 0) > 0 ? '#dc2626' : '#64748b' }}>{v || 0}</span> },
  { key: 'otherClientInteractions', label: 'Other Clients', align: 'right', render: (v) => v || 0 },
  { key: 'uniqueLeads', label: 'Unique Leads', align: 'right', render: (v) => v || 0 },
  { key: 'uniqueClients', label: 'Unique Clients', align: 'right', render: (v) => v || 0 },
  { key: 'calls', label: 'Calls', align: 'right', render: (v) => v || 0 },
  { key: 'visits', label: 'Visits', align: 'right', render: (v) => v || 0 },
  { key: 'sms', label: 'SMS', align: 'right', render: (v) => v || 0 },
  { key: 'whatsapp', label: 'WhatsApp', align: 'right', render: (v) => v || 0 },
  { key: 'emails', label: 'Email', align: 'right', render: (v) => v || 0 },
  { key: 'lastInteractionAt', label: 'Last Interaction', render: (v) => v ? formatDate(v) : '—' },
];

const COLLECTION_RATE_COLUMNS = [
  { key: 'month', label: 'Disbursement Month' },
  { key: 'totalDisbursed', label: 'Total Disbursed', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'totalPayable', label: 'Loan + Interest + Penalties', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'actualPaid', label: 'Actually Paid', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'outstandingAmount', label: 'Still Needed', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'target95Amount', label: '95% Target', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'amountTo95', label: 'To Reach 95%', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'target98Amount', label: '98% Target', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'amountTo98', label: 'To Reach 98%', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'amountTo100', label: 'To Reach 100%', align: 'right', render: (v) => formatCurrency(v || 0) },
  { key: 'rateDisplay', label: 'Collection Rate', align: 'right', render: (v, row) => <RateCell value={v} color={row.color || getAchievementColor(row.rate)} /> },
  { key: 'loanCount', label: 'Loans', align: 'right' },
  { key: 'paymentCount', label: 'Payments', align: 'right' },
];

const getColumns = (type) => {
  switch (type) {
    case 'loans': return LOAN_COLUMNS;
    case 'repayments': return REPAYMENT_COLUMNS;
    case 'closed-loans': case 'closed-loans-export': return CLOSED_LOAN_COLUMNS;
    case 'clients': return CLIENT_COLUMNS;
    case 'leads': return LEAD_COLUMNS;
    case 'applications': return APPLICATION_COLUMNS;
    case 'audit': return AUDIT_COLUMNS;
    case 'officer': return OFFICER_COLUMNS;
    case 'officer-workload': return OFFICER_WORKLOAD_COLUMNS;
    case 'past-maturity-overdue': return PAST_MATURITY_COLUMNS;
    case 'collection-rate': return COLLECTION_RATE_COLUMNS;
    default: return LOAN_COLUMNS;
  }
};

// ═══════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════
const RateCell = ({ value, color }) => (
  <span style={{ ...S.rateBadge, backgroundColor: color + '20', color: color, border: `1px solid ${color}40` }}>{value}</span>
);

const renderRows = (rows, columns) => {
  if (!rows || rows.length === 0) return <tr><td colSpan={columns.length} style={S.emptyState}>No records found</td></tr>;
  return rows.map((row, i) => (
    <tr key={row._id || i}>
      {columns.map(col => (
        <td key={col.key} style={col.align === 'right' ? S.tdRight : (col.key.includes('Number') || col.key.includes('number') || col.key.includes('No') ? S.tdMono : S.td)}>
          {col.render ? col.render(row[col.key], row) : (row[col.key] || '—')}
        </td>
      ))}
    </tr>
  ));
};

// ═══════════════════════════════════════════════════════════════════════
// FLATTEN HELPER - converts nested objects to strings for Excel export
// ═══════════════════════════════════════════════════════════════════════
const isIdentifierExportKey = (key = '') => {
  const normalized = String(key).replace(/[_-]/g, '').toLowerCase();
  return normalized === 'clientnumber' || normalized === 'clientid' || normalized === 'clientidentifier';
};

const flattenRowForExport = (row, headers) => {
  const flat = {};
  headers.forEach(h => {
    let val = row[h.key];
    if (val === null || val === undefined) {
      flat[h.key] = '';
    } else if (typeof val === 'object' && !Array.isArray(val)) {
      flat[h.key] = val.name || val.firstName || val.label || val.branchName || val.clientName || val.branch || val.client || JSON.stringify(val);
    } else if (Array.isArray(val)) {
      flat[h.key] = val.map(item => typeof item === 'object' && item ? (item.name || item.firstName || item.label || JSON.stringify(item)) : item).join(', ');
    } else if (typeof val === 'boolean') {
      flat[h.key] = val ? 'Yes' : 'No';
    } else {
      flat[h.key] = String(val);
    }
  });
  return flat;
};

// Client IDs/numbers are identifiers, not amounts. Keep them as text so
// Excel never changes 32226441 into 32,226,441.
const prepareExportHeaders = (headers) => headers.map(h => ({
  ...h,
  forceString: !!h.forceString || isIdentifierExportKey(h.key),
}));

// ═══════════════════════════════════════════════════════════════════════
// BUILD QUERY PARAMS - shared between display fetch and export fetch
// ═══════════════════════════════════════════════════════════════════════
const buildQueryParams = (reportConfig, filtersObj, pageOverride, limitOverride) => {
  const r = reportConfig;
  const params = new URLSearchParams({ 
    page: pageOverride || 1, 
    limit: limitOverride || 100 
  });
  if (filtersObj.branch) params.set('branch', filtersObj.branch);
  if (filtersObj.search) params.set('search', filtersObj.search);
  if (filtersObj.paymentSource) params.set('paymentSource', filtersObj.paymentSource);
  if (filtersObj.module || r.defaultModule) params.set('module', filtersObj.module || r.defaultModule);
  if (filtersObj.action) params.set('action', filtersObj.action);
  if (filtersObj.officerType && filtersObj.officerType !== 'all') params.set('officerType', filtersObj.officerType);
  if (filtersObj.groupByBranch) params.set('groupByBranch', 'true');
  if (filtersObj.startDate) params.set('startDate', filtersObj.startDate);
  if (filtersObj.endDate) params.set('endDate', filtersObj.endDate);

  if (r.type === 'closed-loans' || r.type === 'closed-loans-export') {
    if (filtersObj.closedStatus) params.set('status', filtersObj.closedStatus);
    else if (r.defaultClosedStatus) params.set('status', r.defaultClosedStatus);
  } else {
    const statusVal = filtersObj.status || r.defaultStatus || '';
    if (statusVal && statusVal !== 'all') params.set('status', statusVal);
  }

  if (filtersObj.leadStatus || r.defaultLeadStatus) params.set('status', filtersObj.leadStatus || r.defaultLeadStatus);
  if (filtersObj.appStatus || r.defaultAppStatus) params.set('status', filtersObj.appStatus || r.defaultAppStatus);
  if (r.type === 'clients' && (filtersObj.status || r.defaultClientStatus)) params.set('status', filtersObj.status || r.defaultClientStatus);
  if (r.type === 'closed-loans' || r.type === 'closed-loans-export') params.set('sort', '-closedAt');

  return params;
};

// ═══════════════════════════════════════════════════════════════════════
// EXTRACT ROWS FROM RESPONSE - shared helper
// ═══════════════════════════════════════════════════════════════════════
const extractRowsFromResponse = (d) => {
  // Officer performance returns grouped arrays inside an object:
  // { loanOfficers, collectionOfficers, allOfficers, summary }.
  // The generic fallback to `d.data || d` can therefore return an object,
  // which later gets passed to renderRows() and causes `.map is not a function`.
  if (Array.isArray(d?.allOfficers)) return d.allOfficers;
  if (Array.isArray(d?.loanOfficers) || Array.isArray(d?.collectionOfficers)) {
    return [
      ...(Array.isArray(d?.loanOfficers) ? d.loanOfficers : []),
      ...(Array.isArray(d?.collectionOfficers) ? d.collectionOfficers : []),
    ];
  }

  const candidates = [
    d?.loans, d?.repayments, d?.clients, d?.leads, d?.applications,
    d?.logs, d?.auditLogs, d?.rows, d?.months,
    d?.data, d,
  ];

  const arrayValue = candidates.find(Array.isArray);
  return arrayValue || [];
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
export default function Reports() {
  const [activeReport, setActiveReport] = useState(null);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    branch: '', status: '', search: '', startDate: '', endDate: '',
    paymentSource: '', module: '', action: '', officerType: 'all',
    closedStatus: '', leadStatus: '', appStatus: '', groupByBranch: false,
  });
  const [reportData, setReportData] = useState(null);
  const [reportRows, setReportRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [page, setPage] = useState(1);
  const [reportSummary, setReportSummary] = useState(null);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const exportAbortRef = useRef(null);

  useEffect(() => {
    api.get('/branches?isActive=true').then(r => setBranches(r.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeReport) return;
    setPage(1);
    fetchReportData(1);
  }, [activeReport]);

  useEffect(() => {
    if (!activeReport || activeReport.type === 'branch-performance' || activeReport.type === 'past-maturity-overdue') return;
    fetchReportData(page);
  }, [page]);

  // ═══════════════════════════════════════════════════════════════════════
  // FETCH REPORT DATA (for display — paginated)
  // ═══════════════════════════════════════════════════════════════════════
  const fetchReportData = useCallback(async (p) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    setReportSummary(null);

    const r = activeReport;
    const params = buildQueryParams(r, filters, p, 100);

    try {
      const res = await api.get(r.endpoint + '?' + params.toString(), { signal: controller.signal });
      const d = res.data.data;

      if (r.type === 'branch-performance') {
        setReportData(d);
        setReportRows([]);
        setPagination(null);
        setReportSummary(null);
      } else if (r.type === 'past-maturity-overdue') {
        setReportData(d);
        setReportRows(d.loans || []);
        setPagination(null);
        setReportSummary(d.summary || null);
      } else {
        const rows = extractRowsFromResponse(d);
        const pag = d.pagination || null;
        const summary = d.stats || d.summary || null;
        setReportRows(rows);
        setPagination(pag);
        setReportSummary(summary);
        setReportData(d);
      }
    } catch (err) {
      if (err.name !== 'Canceled' && err.name !== 'AbortError') {
        setError(err.response?.data?.message || err.message || 'Failed to load report');
      }
    } finally {
      setLoading(false);
    }
  }, [activeReport, filters, page]);

  const handleRunReport = () => {
    setPage(1);
    fetchReportData(1);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // FETCH ALL DATA FOR EXPORT — loops through all pages
  // ═══════════════════════════════════════════════════════════════════════
  const fetchAllDataForExport = async () => {
    if (exportAbortRef.current) exportAbortRef.current.abort();
    const controller = new AbortController();
    exportAbortRef.current = controller;

    const r = activeReport;
    const PAGE_SIZE = 1000;
    let allData = [];
    let currentPage = 1;
    let hasMore = true;
    let totalRecords = null;

    while (hasMore) {
      const params = buildQueryParams(r, filters, currentPage, PAGE_SIZE);

      const res = await api.get(r.endpoint + '?' + params.toString(), { signal: controller.signal });
      const d = res.data.data;
      const pageData = extractRowsFromResponse(d);

      if (Array.isArray(pageData) && pageData.length > 0) {
        allData = allData.concat(pageData);

        const pag = d.pagination;
        if (pag && pag.pages && pag.total) {
          totalRecords = pag.total;
          setExportProgress(`Fetching ${allData.length.toLocaleString()} of ${pag.total.toLocaleString()} records...`);

          if (currentPage < pag.pages) {
            currentPage++;
          } else {
            hasMore = false;
          }
        } else {
          // No pagination info — if we got fewer than PAGE_SIZE, we're done
          if (pageData.length < PAGE_SIZE) {
            hasMore = false;
          } else {
            currentPage++;
            // Safety: stop after 200 pages
            if (currentPage > 200) {
              console.warn('[Export] Stopped after 200 pages to prevent infinite loop');
              hasMore = false;
            }
          }
        }
      } else {
        hasMore = false;
      }
    }

    setExportProgress(null);
    console.log(`[Export] Fetched ${allData.length.toLocaleString()} total records across ${currentPage} page(s)`);
    return allData;
  };

  // ═══════════════════════════════════════════════════════════════════════
  // EXPORT — fetches ALL records then sends to backend for Excel generation
  // ═══════════════════════════════════════════════════════════════════════
  const handleExport = async () => {
    if (!activeReport) return;
    setExporting(true);
    setExportProgress('Preparing export...');
    setError(null);

    try {
      let exportData = [];
      let exportHeaders = getColumns(activeReport.type).map(c => ({ key: c.key, label: c.label }));

      // ═══════════════════════════════════════════════════════════
      // BRANCH PERFORMANCE — data is already fully loaded (not paginated)
      // ═══════════════════════════════════════════════════════════
      if (activeReport.type === 'branch-performance') {
        if (reportData?.dailyData) {
          exportData = reportData.dailyData;
          exportHeaders = [
            { key: 'date', label: 'Date' },
            { key: 'loanCount', label: 'Loans' },
            { key: 'totalDisbursed', label: 'Total Disbursed' },
            { key: 'totalPaid', label: 'Total Collected' },
            { key: 'paymentCount', label: 'Payments' },
            { key: 'cumulativeLoans', label: 'Cum. Loans' },
            { key: 'cumulativeDisbursed', label: 'Cum. Disbursed' },
            { key: 'cumulativePaid', label: 'Cum. Collected' },
          ];
        } else if (reportData?.performance) {
          exportData = reportData.performance;
          exportHeaders = [
            { key: 'branchName', label: 'Branch' },
            { key: 'target', label: 'Target' },
            { key: 'actual', label: 'Actual' },
            { key: 'variance', label: 'Variance' },
            { key: 'rate', label: '% Achieved' },
          ];
        } else if (reportData?.loanOfficers?.data) {
          exportData = reportData.loanOfficers.data;
          exportHeaders = [
            { key: 'officerName', label: 'Officer' },
            { key: 'officerType', label: 'Type' },
            { key: 'branchName', label: 'Branch' },
            { key: 'target', label: 'Target' },
            { key: 'actual', label: 'Actual' },
            { key: 'metric', label: 'Metric' },
            { key: 'rate', label: '% Achieved' },
          ];
        } else if (reportData?.collectionOfficers?.data) {
          exportData = reportData.collectionOfficers.data;
          exportHeaders = [
            { key: 'officerName', label: 'Officer' },
            { key: 'officerType', label: 'Type' },
            { key: 'branchName', label: 'Branch' },
            { key: 'target', label: 'Target' },
            { key: 'actual', label: 'Actual' },
            { key: 'metric', label: 'Metric' },
            { key: 'rate', label: '% Achieved' },
          ];
        } else if (reportData?.branchSummary) {
          exportData = reportData.branchSummary;
          exportHeaders = [
            { key: 'branchName', label: 'Branch' },
            { key: 'totalLoans', label: 'Loans' },
            { key: 'totalDisbursed', label: 'Total Disbursed' },
            { key: 'avgPerDay', label: 'Avg/Day' },
            { key: 'rate', label: '% vs Target' },
          ];
        }
      }
      // ═══════════════════════════════════════════════════════════
      // PAST MATURITY — data is already fully loaded (not paginated)
      // ═══════════════════════════════════════════════════════════
      else if (activeReport.type === 'past-maturity-overdue' && reportData?.loans) {
        exportData = reportData.loans;
      }
      // ═══════════════════════════════════════════════════════════
      // ALL PAGINATED REPORTS — fetch ALL pages
      // ═══════════════════════════════════════════════════════════
      else {
        setExportProgress('Fetching all records...');
        exportData = await fetchAllDataForExport();
      }

      if (!exportData || exportData.length === 0) {
        setError('No data to export. Try adjusting your filters.');
        setExporting(false);
        setExportProgress(null);
        return;
      }

      // ═══════════════════════════════════════════════════════════
      // Flatten nested objects into strings for Excel
      // ═══════════════════════════════════════════════════════════
      setExportProgress(`Generating Excel with ${exportData.length.toLocaleString()} records...`);
      const exportHeadersWithTypes = prepareExportHeaders(exportHeaders);
      const flatData = exportData.map(row => flattenRowForExport(row, exportHeadersWithTypes));

      // ═══════════════════════════════════════════════════════════
      // Send to backend export endpoint
      // ═══════════════════════════════════════════════════════════
      const payload = {
        data: flatData,
        headers: exportHeadersWithTypes,
        filename: activeReport.name,
        format: 'xlsx',
      };

      const res = await api.post('/reports/exports/generate', payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeReport.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      if (err.name !== 'Canceled' && err.name !== 'AbortError') {
        console.error('[Export] Error:', err);
        setError('Export failed: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // CANCEL EXPORT (if user navigates away or clicks back)
  // ═══════════════════════════════════════════════════════════════════════
  const handleCancelExport = () => {
    if (exportAbortRef.current) {
      exportAbortRef.current.abort();
      exportAbortRef.current = null;
    }
    setExporting(false);
    setExportProgress(null);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER: Report List (home view)
  // ═══════════════════════════════════════════════════════════════════════
  const renderReportList = () => (
    <div style={S.page}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: 0 }}>Reports</h1>
        <p style={{ fontSize: 14, color: '#64748b', margin: '4px 0 0 0' }}>Select a report to view data, apply filters, and export</p>
      </div>
      {REPORT_CATEGORIES.map(cat => (
        <div key={cat.title} style={S.catCard}>
          <div style={S.catHeader}>
            <span style={{ fontSize: 20 }}>{cat.icon}</span>
            <span style={S.catTitle}>{cat.title}</span>
            <span style={S.catCount}>{cat.reports.length} reports</span>
          </div>
          {cat.reports.map(rep => (
            <div key={rep.id} style={{ ...S.reportRow, background: 'transparent' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div>
                <div style={S.reportName}>{rep.name}</div>
                <div style={S.reportDesc}>{rep.desc}</div>
              </div>
              <button style={S.viewBtn} onClick={() => {
                setFilters({ branch: '', status: '', search: '', startDate: '', endDate: '', paymentSource: '', module: '', action: '', officerType: 'all', closedStatus: '', leadStatus: '', appStatus: '', groupByBranch: false });
                setActiveReport(rep);
              }}>
                View →
              </button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER: Filter Bar
  // ═══════════════════════════════════════════════════════════════════════
  const renderFilterBar = () => {
    const r = activeReport;
    if (!r) return null;
    const f = r.filters || [];
    const isDisabled = exporting || loading;

    return (
      <div style={S.filterBar}>
        {f.includes('branch') && (
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Branch</label>
            <select style={S.filterSelect} value={filters.branch} onChange={e => setFilters(p => ({ ...p, branch: e.target.value }))} disabled={isDisabled}>
              <option value="">All Branches</option>
              {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
          </div>
        )}
        {f.includes('status') && (
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Status</label>
            <select style={S.filterSelect} value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))} disabled={isDisabled}>
              {LOAN_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}
        {f.includes('closedStatus') && (
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Closed Status</label>
            <select style={S.filterSelect} value={filters.closedStatus} onChange={e => setFilters(p => ({ ...p, closedStatus: e.target.value }))} disabled={isDisabled}>
              {CLOSED_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}
        {f.includes('leadStatus') && (
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Lead Status</label>
            <select style={S.filterSelect} value={filters.leadStatus} onChange={e => setFilters(p => ({ ...p, leadStatus: e.target.value }))} disabled={isDisabled}>
              {LEAD_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}
        {f.includes('appStatus') && (
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>App. Status</label>
            <select style={S.filterSelect} value={filters.appStatus} onChange={e => setFilters(p => ({ ...p, appStatus: e.target.value }))} disabled={isDisabled}>
              {APP_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}
        {f.includes('paymentSource') && (
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Payment Source</label>
            <select style={S.filterSelect} value={filters.paymentSource} onChange={e => setFilters(p => ({ ...p, paymentSource: e.target.value }))} disabled={isDisabled}>
              {PAYMENT_SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}
        {f.includes('module') && (
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Module</label>
            <select style={S.filterSelect} value={filters.module} onChange={e => setFilters(p => ({ ...p, module: e.target.value }))} disabled={isDisabled}>
              {MODULE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}
        {f.includes('action') && (
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Action</label>
            <select style={S.filterSelect} value={filters.action} onChange={e => setFilters(p => ({ ...p, action: e.target.value }))} disabled={isDisabled}>
              {ACTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}
        {f.includes('officerType') && (
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Officer Type</label>
            <select style={S.filterSelect} value={filters.officerType} onChange={e => setFilters(p => ({ ...p, officerType: e.target.value }))} disabled={isDisabled}>
              {OFFICER_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}
        {f.includes('search') && (
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>Search</label>
            <input style={S.filterInput} placeholder="Loan No, Client, Phone..." value={filters.search} onChange={e => setFilters(p => ({ ...p, search: e.target.value }))} disabled={isDisabled} />
          </div>
        )}
        {f.includes('dateRange') && (
          <>
            <div style={S.filterGroup}>
              <label style={S.filterLabel}>From</label>
              <input type="date" style={S.filterDate} value={filters.startDate} onChange={e => setFilters(p => ({ ...p, startDate: e.target.value }))} disabled={isDisabled} />
            </div>
            <div style={S.filterGroup}>
              <label style={S.filterLabel}>To</label>
              <input type="date" style={S.filterDate} value={filters.endDate} onChange={e => setFilters(p => ({ ...p, endDate: e.target.value }))} disabled={isDisabled} />
            </div>
          </>
        )}
        {f.includes('closedDateRange') && (
          <>
            <div style={S.filterGroup}>
              <label style={S.filterLabel}>Closed From</label>
              <input type="date" style={S.filterDate} value={filters.startDate} onChange={e => setFilters(p => ({ ...p, startDate: e.target.value }))} disabled={isDisabled} />
            </div>
            <div style={S.filterGroup}>
              <label style={S.filterLabel}>Closed To</label>
              <input type="date" style={S.filterDate} value={filters.endDate} onChange={e => setFilters(p => ({ ...p, endDate: e.target.value }))} disabled={isDisabled} />
            </div>
          </>
        )}
        {f.includes('groupByBranch') && (
          <div style={S.checkboxGroup}>
            <input type="checkbox" id="groupByBranch" checked={filters.groupByBranch} onChange={e => setFilters(p => ({ ...p, groupByBranch: e.target.checked }))} disabled={isDisabled} />
            <label htmlFor="groupByBranch" style={S.checkboxLabel}>Group by Branch</label>
          </div>
        )}
        <div style={S.filterGroup}>
          <label style={S.filterLabel}>&nbsp;</label>
          <button style={{ ...S.btnPrimary, opacity: loading ? 0.6 : 1 }} onClick={handleRunReport} disabled={loading || exporting}>
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        </div>
        <div style={S.filterGroup}>
          <label style={S.filterLabel}>&nbsp;</label>
          {exporting ? (
            <button style={S.btnExportDisabled} onClick={handleCancelExport}>
              <span style={S.exportSpinner} />
              {exportProgress || 'Exporting...'}
            </button>
          ) : (
            <button style={S.btnExport} onClick={handleExport} disabled={loading}>
              📥 Export Excel
            </button>
          )}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER: Export Progress Banner
  // ═══════════════════════════════════════════════════════════════════════
  const renderExportProgress = () => {
    if (!exporting || !exportProgress) return null;
    return (
      <div style={S.infoBox}>
        <span style={S.exportSpinner} />
        <span>{exportProgress}</span>
        <button 
          onClick={handleCancelExport} 
          style={{ marginLeft: 'auto', background: 'none', border: '1px solid #93c5fd', borderRadius: 4, padding: '2px 10px', cursor: 'pointer', color: '#2563eb', fontSize: 12, fontWeight: 600 }}
        >
          Cancel
        </button>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER: Summary Cards
  // ═══════════════════════════════════════════════════════════════════════
  const renderSummaryCards = () => {
    const s = reportSummary;
    if (!s) return null;
    const type = activeReport?.type;
    let cards = [];

    if (type === 'loans') {
      cards = [
        { label: 'Total Loans', value: s.totalActive || 0, color: '#1e293b' },
        { label: 'Active', value: s.activeCount || 0, color: '#059669' },
        { label: 'Overdue', value: s.overdueCount || 0, color: '#dc2626' },
        { label: 'Defaulted', value: s.defaultedCount || 0, color: '#7f1d1d' },
        { label: 'Total Disbursed', value: formatCurrency(s.totalPrincipalDisbursed), color: '#2563eb' },
        { label: 'Total Outstanding', value: formatCurrency(s.totalOutstanding), color: '#dc2626' },
        { label: 'Total Collected', value: formatCurrency(s.totalCollected), color: '#059669' },
        { label: 'Avg Days', value: `${s.avgDays || 0}d`, color: '#64748b' },
      ];
    } else if (type === 'officer-workload') {
      cards = [
        { label: 'Total Interactions', value: s.totalInteractions || 0, color: '#4f46e5' },
        { label: 'Lead Interactions', value: s.totalLeadInteractions || 0, color: '#2563eb' },
        { label: 'Client Interactions', value: s.totalClientInteractions || 0, color: '#059669' },
        { label: 'Active-Loan', value: s.activeLoanInteractions || 0, color: '#0f766e' },
        { label: 'Overdue-Loan', value: s.overdueLoanInteractions || 0, color: '#dc2626' },
        { label: 'Officers Active', value: s.officersWithActivity || 0, color: '#7c3aed' },
      ];
    } else if (type === 'repayments') {
      cards = [
        { label: 'Total Payments', value: s.paymentCount || 0, color: '#1e293b' },
        { label: 'Total Collected', value: formatCurrency(s.totalAmount || s.totalCollected || 0), color: '#059669' },
        { label: 'Principal', value: formatCurrency(s.principalAmount || s.totalPrincipalCollected || 0), color: '#2563eb' },
        { label: 'Interest', value: formatCurrency(s.interestAmount || s.totalInterestCollected || 0), color: '#d97706' },
        { label: 'Penalty', value: formatCurrency(s.penaltyAmount || s.totalPenaltyCollected || 0), color: '#dc2626' },
        { label: 'Unique Clients', value: s.uniqueClientCount || 0, color: '#64748b' },
      ];
    } else if (type === 'closed-loans' || type === 'closed-loans-export') {
      cards = [
        { label: 'Total Closed', value: s.totalClosed || 0, color: '#1e293b' },
        { label: 'Fully Paid', value: s.fullyPaidCount || 0, color: '#059669' },
        { label: 'Partial Payment', value: s.partiallyPaidCount || 0, color: '#d97706' },
        { label: 'Principal Disbursed', value: formatCurrency(s.totalPrincipalDisbursed), color: '#2563eb' },
        { label: 'Total Collected', value: formatCurrency(s.totalAmountCollected), color: '#059669' },
        { label: 'Outstanding at Close', value: formatCurrency(s.totalOutstandingAtClose), color: '#dc2626' },
        { label: 'Avg Days to Close', value: `${s.avgDaysToClose || 0}d`, color: '#64748b' },
        { label: 'Collection Rate', value: `${s.collectionRate || 0}%`, color: s.collectionRate >= 80 ? '#059669' : '#dc2626' },
      ];
    }

    if (cards.length === 0) return null;
    return (
      <div style={S.summaryBar}>
        {cards.map((c, i) => (
          <div key={i} style={S.summaryCard}>
            <div style={S.summaryLabel}>{c.label}</div>
            <div style={{ ...S.summaryValue, color: c.color, fontSize: c.value.toString().length > 12 ? 16 : 20 }}>{c.value}</div>
          </div>
        ))}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER: Standard Table with Pagination
  // ═══════════════════════════════════════════════════════════════════════
  const renderStandardTable = () => {
    const columns = getColumns(activeReport?.type);
    return (
      <>
        {renderSummaryCards()}
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {columns.map(col => (
                  <th key={col.key} style={col.align === 'right' ? S.thRight : S.th}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>{renderRows(reportRows, columns)}</tbody>
          </table>
        </div>
        {pagination && pagination.pages > 1 && (
          <div style={S.pagination}>
            <span>Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button style={{ ...S.pageBtn, ...(pagination.page <= 1 ? S.pageBtnDisabled : {}) }} disabled={pagination.page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              {Array.from({ length: Math.min(pagination.pages, 7) }, (_, i) => {
                let pageNum;
                if (pagination.pages <= 7) { pageNum = i + 1; }
                else if (pagination.page <= 4) { pageNum = i + 1; }
                else if (pagination.page >= pagination.pages - 3) { pageNum = pagination.pages - 6 + i; }
                else { pageNum = pagination.page - 3 + i; }
                return <button key={pageNum} style={pageNum === pagination.page ? S.pageBtnActive : S.pageBtn} onClick={() => setPage(pageNum)}>{pageNum}</button>;
              })}
              <button style={{ ...S.pageBtn, ...(pagination.page >= pagination.pages ? S.pageBtnDisabled : {}) }} disabled={pagination.page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        )}
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER: Performance Dashboard (branch-performance type)
  // ═══════════════════════════════════════════════════════════════════════
  const PerfTable = ({ title, data, columns, totals }) => (
    <div style={S.perfCard}>
      <div style={S.perfHeader}>
        <span>{title}</span>
        {totals && <span style={{ fontSize: 12, opacity: 0.8 }}>{data?.length || 0} rows</span>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ ...S.table, fontSize: 12 }}>
          <thead>
            <tr>
              {columns.map((c, i) => <th key={i} style={c.right ? S.perfThRight : S.perfTh}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {data && data.length > 0 ? data.map((row, ri) => (
              <tr key={ri}>
                {columns.map((c, ci) => (
                  <td key={ci} style={c.right ? S.perfTdRight : S.perfTd}>
                    {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            )) : <tr><td colSpan={columns.length} style={S.emptyState}>No data</td></tr>}
            {totals && (
              <tr style={S.perfTotalsRow}>
                {columns.map((c, ci) => (
                  <td key={ci} style={c.right ? S.perfTdRight : S.perfTd}>
                    {c.render ? c.render(totals[c.key], totals) : (totals[c.key] ?? '')}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderPerformanceDashboard = () => {
    const d = reportData;
    if (!d) return <div style={S.emptyState}>No data</div>;
    const rid = activeReport?.id;

    // LEADS UPDATE
    if (rid === 'dash-leads') {
      return <PerfTable title={d.title} data={d.performance} columns={[
        { key: 'branchName', label: 'Branch' },
        { key: 'target', label: 'Target', right: true },
        { key: 'actual', label: 'Actual', right: true },
        { key: 'variance', label: 'Variance', right: true, render: (v) => <span style={{ color: v >= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>{v >= 0 ? '+' : ''}{v}</span> },
        { key: 'rate', label: '% Achieved', right: true, render: (v, row) => <RateCell value={v} color={row.color} /> },
      ]} totals={d.totals} />;
    }

    // DAILY NEW LOANS PERFORMANCE
    if (rid === 'dash-daily-loans') {
      return (
        <>
          {d.totals && (
            <div style={S.summaryBar}>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Total Loans</div><div style={{ ...S.summaryValue, color: '#2563eb' }}>{d.totals.totalLoans}</div></div>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Total Disbursed</div><div style={{ ...S.summaryValue, color: '#059669' }}>{formatCurrency(d.totals.totalDisbursed)}</div></div>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Period Target</div><div style={S.summaryValue}>{d.totals.periodTarget}</div></div>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Achievement</div><div style={{ ...S.summaryValue, color: d.totals.totalColor }}>{d.totals.totalRateStr}</div></div>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Avg/Day</div><div style={S.summaryValue}>{d.totals.avgPerDay}</div></div>
            </div>
          )}
          {d.branchSummary && <PerfTable title="BRANCH SUMMARY" data={d.branchSummary} columns={[
            { key: 'branchName', label: 'Branch' },
            { key: 'totalLoans', label: 'Loans', right: true },
            { key: 'totalDisbursed', label: 'Disbursed', right: true, render: (v) => formatCurrency(v) },
            { key: 'avgPerDay', label: 'Avg/Day', right: true },
            { key: 'rate', label: '% vs Target', right: true, render: (v, row) => <RateCell value={v} color={row.color} /> },
          ]} />}
          {d.dailyData && <PerfTable title={`DAILY DATA (${d.period?.start} to ${d.period?.end})`} data={d.dailyData.slice(0, 200)} columns={[
            { key: 'date', label: 'Date' },
            { key: 'loanCount', label: 'Loans', right: true },
            { key: 'totalDisbursed', label: 'Disbursed', right: true, render: (v) => formatCurrency(v) },
            { key: 'cumulativeLoans', label: 'Cum. Loans', right: true },
            { key: 'cumulativeDisbursed', label: 'Cum. Disbursed', right: true, render: (v) => formatCurrency(v) },
            { key: 'dailyRateStr', label: 'Daily %', right: true, render: (v, row) => <RateCell value={v} color={row.dailyColor} /> },
            { key: 'cumulativeRateStr', label: 'Cum. %', right: true, render: (v, row) => <RateCell value={v} color={row.cumulativeColor} /> },
          ]} />}
        </>
      );
    }

    // DAILY COLLECTIONS REPORT
    if (rid === 'dash-eom-collections') {
      const branchCollColumns = [
        { key: 'branchName', label: 'Branch' },
        { key: 'payableAmount', label: 'Payable', right: true, render: (v) => formatCurrency(v) },
        { key: 'totalPaid', label: 'Collected', right: true, render: (v) => formatCurrency(v) },
        { key: 'balance', label: 'Balance', right: true, render: (v) => <span style={{ color: v > 0 ? '#dc2626' : '#059669', fontWeight: 700 }}>{formatCurrency(v)}</span> },
        { key: 'collectionRate', label: 'Rate', right: true, render: (v, row) => <RateCell value={v} color={row.color} /> },
      ];
      const dailyCollColumns = [
        { key: 'date', label: 'Date' },
        { key: 'totalPaid', label: 'Collected', right: true, render: (v) => formatCurrency(v) },
        { key: 'principalPaid', label: 'Principal', right: true, render: (v) => formatCurrency(v) },
        { key: 'interestPaid', label: 'Interest', right: true, render: (v) => formatCurrency(v) },
        { key: 'paymentCount', label: 'Payments', right: true },
        { key: 'cumulativePaid', label: 'Cum. Collected', right: true, render: (v) => formatCurrency(v) },
      ];
      return (
        <>
          {d.totals && (
            <div style={S.summaryBar}>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Total Collected</div><div style={{ ...S.summaryValue, color: '#059669' }}>{formatCurrency(d.totals.totalPaid)}</div></div>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Total Payable</div><div style={{ ...S.summaryValue, color: '#2563eb' }}>{formatCurrency(d.totals.payableAmount)}</div></div>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Balance</div><div style={{ ...S.summaryValue, color: d.totals.balance > 0 ? '#dc2626' : '#059669' }}>{formatCurrency(d.totals.balance)}</div></div>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Collection Rate</div><div style={{ ...S.summaryValue, color: d.totals.collectionColor }}>{d.totals.collectionRateStr}</div></div>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Avg/Day</div><div style={S.summaryValue}>{formatCurrency(d.totals.avgPerDay)}</div></div>
            </div>
          )}
          {d.branchSummary && <PerfTable title="BRANCH COLLECTION SUMMARY" data={d.branchSummary} columns={branchCollColumns} />}
          {d.dailyData && <PerfTable title={`DAILY COLLECTIONS (${d.period?.start} to ${d.period?.end})`} data={d.dailyData.slice(0, 200)} columns={dailyCollColumns} />}
        </>
      );
    }

    // ONBOARDING BONUS RESULTS
    if (rid === 'dash-onboarding-bonus') {
      const officerCols = [
        { key: 'officerName', label: 'Officer' },
        { key: 'officerType', label: 'Type' },
        { key: 'branchName', label: 'Branch' },
        { key: 'target', label: 'Target', right: true },
        { key: 'actual', label: 'Actual', right: true },
        { key: 'metric', label: 'Metric' },
        { key: 'variance', label: 'Variance', right: true, render: (v) => <span style={{ color: v >= 0 ? '#059669' : '#dc2626', fontWeight: 700 }}>{v >= 0 ? '+' : ''}{v}</span> },
        { key: 'rate', label: '%', right: true, render: (v, row) => <RateCell value={v} color={row.color} /> },
      ];
      return (
        <>
          {d.grandTotals && (
            <div style={S.summaryBar}>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Grand Total Actual</div><div style={{ ...S.summaryValue, color: '#2563eb' }}>{d.grandTotals.actual}</div></div>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Grand Target</div><div style={S.summaryValue}>{d.grandTotals.target}</div></div>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Achievement</div><div style={{ ...S.summaryValue, color: d.grandTotals.color }}>{d.grandTotals.rate}</div></div>
            </div>
          )}
          {d.loanOfficers?.data?.length > 0 && <PerfTable title="LOAN OFFICERS" data={d.loanOfficers.data} columns={officerCols} totals={d.loanOfficers.totals} />}
          {d.collectionOfficers?.data?.length > 0 && <PerfTable title="COLLECTION OFFICERS" data={d.collectionOfficers.data} columns={officerCols} totals={d.collectionOfficers.totals} />}
        </>
      );
    }

    // BRANCH DISBURSEMENT & COLLECTION SUMMARY
    if (rid === 'dash-branch-summary') {
      return (
        <>
          {d.totals && (
            <div style={S.summaryBar}>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Loans Disbursed</div><div style={{ ...S.summaryValue, color: '#2563eb' }}>{d.totals.totalLoans}</div></div>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Total Disbursed</div><div style={{ ...S.summaryValue, color: '#059669' }}>{formatCurrency(d.totals.totalDisbursed)}</div></div>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Total Collected</div><div style={{ ...S.summaryValue, color: '#059669' }}>{formatCurrency(d.totals.totalPaid)}</div></div>
              <div style={S.summaryCard}><div style={S.summaryLabel}>Achievement</div><div style={{ ...S.summaryValue, color: d.totals.totalColor }}>{d.totals.totalRateStr}</div></div>
            </div>
          )}
          {d.dailyData && <PerfTable title={`DAILY DISBURSEMENT & COLLECTIONS (${d.period?.start} to ${d.period?.end})`} data={d.dailyData.slice(0, 200)} columns={[
            { key: 'date', label: 'Date' },
            { key: 'loanCount', label: 'Loans', right: true },
            { key: 'totalDisbursed', label: 'Disbursed', right: true, render: (v) => formatCurrency(v) },
            { key: 'cumulativeLoans', label: 'Cum. Loans', right: true },
            { key: 'dailyRateStr', label: 'Daily %', right: true, render: (v, row) => <RateCell value={v} color={row.dailyColor} /> },
            { key: 'totalPaid', label: 'Collected', right: true, render: (v) => formatCurrency(v) },
            { key: 'cumulativePaid', label: 'Cum. Collected', right: true, render: (v) => formatCurrency(v) },
            { key: 'paymentCount', label: 'Payments', right: true },
          ]} />}
        </>
      );
    }

    return <div style={S.emptyState}>Unknown dashboard type</div>;
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER: Past Maturity & Overdue (special layout)
  // ═══════════════════════════════════════════════════════════════════════
  const renderPastMaturityOverdue = () => {
    const d = reportData;
    if (!d) return <div style={S.emptyState}>No data</div>;
    const s = d.summary || {};

    const getAgingColor = (label) => {
      if (label === 'Due Today') return '#eab308';
      if (label.includes('1-7')) return '#d97706';
      if (label.includes('8-14')) return '#f97316';
      if (label.includes('15-30')) return '#ef4444';
      if (label.includes('31-60')) return '#dc2626';
      if (label.includes('61-90')) return '#b91c1c';
      return '#7f1d1d';
    };

    return (
      <>
        <div style={S.summaryBar}>
          <div style={{ ...S.summaryCard, borderLeft: '4px solid #dc2626' }}>
            <div style={S.summaryLabel}>Past Maturity</div>
            <div style={{ ...S.summaryValue, color: '#dc2626', fontSize: 28 }}>{s.totalPastMaturity || 0}</div>
          </div>
          <div style={{ ...S.summaryCard, borderLeft: '4px solid #dc2626' }}>
            <div style={S.summaryLabel}>Total Outstanding</div>
            <div style={{ ...S.summaryValue, color: '#dc2626' }}>{formatCurrency(s.totalOutstanding)}</div>
          </div>
          <div style={{ ...S.summaryCard, borderLeft: '4px solid #2563eb' }}>
            <div style={S.summaryLabel}>Outstanding Principal</div>
            <div style={{ ...S.summaryValue, color: '#2563eb' }}>{formatCurrency(s.totalOutstandingPrincipal)}</div>
          </div>
          <div style={{ ...S.summaryCard, borderLeft: '4px solid #d97706' }}>
            <div style={S.summaryLabel}>Outstanding Interest</div>
            <div style={{ ...S.summaryValue, color: '#d97706' }}>{formatCurrency(s.totalOutstandingInterest)}</div>
          </div>
          <div style={{ ...S.summaryCard, borderLeft: '4px solid #ef4444' }}>
            <div style={S.summaryLabel}>Outstanding Penalty</div>
            <div style={{ ...S.summaryValue, color: '#ef4444' }}>{formatCurrency(s.totalOutstandingPenalty)}</div>
          </div>
          <div style={{ ...S.summaryCard, borderLeft: '4px solid #059669' }}>
            <div style={S.summaryLabel}>Total Collected</div>
            <div style={{ ...S.summaryValue, color: '#059669' }}>{formatCurrency(s.totalPaid)}</div>
          </div>
          <div style={{ ...S.summaryCard, borderLeft: '4px solid #64748b' }}>
            <div style={S.summaryLabel}>Avg Days Past</div>
            <div style={{ ...S.summaryValue, color: '#64748b' }}>{s.avgDaysPastMaturity || 0}d</div>
          </div>
          <div style={{ ...S.summaryCard, borderLeft: '4px solid #7c3aed' }}>
            <div style={S.summaryLabel}>Collection Rate</div>
            <div style={{ ...S.summaryValue, color: s.collectionRate >= 60 ? '#059669' : '#dc2626' }}>{s.collectionRate || 0}%</div>
          </div>
        </div>

        {d.agingBuckets && d.agingBuckets.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 10px 0' }}>AGING ANALYSIS</h3>
            <div style={S.agingGrid}>
              {d.agingBuckets.map((bucket, i) => {
                const color = getAgingColor(bucket.label);
                return (
                  <div key={i} style={{ ...S.agingCard, borderTop: `3px solid ${color}` }}>
                    <div style={S.agingLabel}>{bucket.label}</div>
                    <div style={{ ...S.agingCount, color }}>{bucket.count || 0}</div>
                    <div style={S.agingAmount}>{formatCurrency(bucket.totalOutstanding || 0)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={S.twoColGrid}>
          <div style={S.perfCard}>
            <div style={S.perfHeader}>STATUS BREAKDOWN</div>
            <div>
              {(d.statusBreakdown || []).map((sb, i) => {
                const st = LOAN_STATUS_STYLES[sb.status] || {};
                return (
                  <div key={i} style={S.statusBreakdownRow}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ ...statusBadge(sb.status), margin: 0 }}>{st.icon || ''} {sb.label}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <span style={{ fontWeight: 700, color: '#1e293b', minWidth: 40, textAlign: 'right' }}>{sb.count}</span>
                      <span style={{ fontFamily: 'monospace', color: '#dc2626', fontWeight: 600, minWidth: 120, textAlign: 'right' }}>{formatCurrency(sb.totalOutstanding)}</span>
                    </div>
                  </div>
                );
              })}
              {(!d.statusBreakdown || d.statusBreakdown.length === 0) && <div style={S.emptyState}>No data</div>}
            </div>
          </div>

          <div style={S.perfCard}>
            <div style={S.perfHeader}>BRANCH BREAKDOWN</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ ...S.table, fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={S.perfTh}>Branch</th>
                    <th style={S.perfThRight}>Count</th>
                    <th style={S.perfThRight}>Outstanding</th>
                    <th style={S.perfThRight}>Principal</th>
                    <th style={S.perfThRight}>Interest</th>
                    <th style={S.perfThRight}>Penalty</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.branchBreakdown || []).map((b, i) => (
                    <tr key={i}>
                      <td style={S.perfTd}>{b.branchName}</td>
                      <td style={{ ...S.perfTdRight, fontWeight: 700 }}>{b.count}</td>
                      <td style={{ ...S.perfTdRight, color: '#dc2626', fontWeight: 600 }}>{formatCurrency(b.totalOutstanding)}</td>
                      <td style={S.perfTdRight}>{formatCurrency(b.outstandingPrincipal)}</td>
                      <td style={{ ...S.perfTdRight, color: '#d97706' }}>{formatCurrency(b.outstandingInterest)}</td>
                      <td style={{ ...S.perfTdRight, color: b.outstandingPenalty > 0 ? '#dc2626' : '#94a3b8' }}>{formatCurrency(b.outstandingPenalty)}</td>
                    </tr>
                  ))}
                  {(!d.branchBreakdown || d.branchBreakdown.length === 0) && <tr><td colSpan={6} style={S.emptyState}>No data</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div style={S.perfCard}>
          <div style={{ ...S.perfHeader, background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)' }}>
            <span>PAST MATURITY LOAN DETAILS</span>
            <span style={{ fontSize: 12, opacity: 0.8 }}>{d.loans?.length || 0} loans</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...S.table, fontSize: 12 }}>
              <thead>
                <tr>
                  {PAST_MATURITY_COLUMNS.map(col => (
                    <th key={col.key} style={col.align === 'right' ? S.thRight : S.th}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.loans && d.loans.length > 0 ? d.loans.map((loan, i) => (
                  <tr key={loan._id || i}>
                    {PAST_MATURITY_COLUMNS.map(col => (
                      <td key={col.key} style={col.align === 'right' ? S.tdRight : (col.key.includes('Number') || col.key.includes('No') ? S.tdMono : S.td)}>
                        {col.render ? col.render(loan[col.key], loan) : (loan[col.key] || '—')}
                      </td>
                    ))}
                  </tr>
                )) : <tr><td colSpan={PAST_MATURITY_COLUMNS.length} style={S.emptyState}>No past maturity loans found</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {d.generatedAt && (
          <div style={{ textAlign: 'center', padding: '12px', color: '#94a3b8', fontSize: 11 }}>
            Report generated at: {new Date(d.generatedAt).toLocaleString()}
          </div>
        )}
      </>
    );
  };

  const renderCollectionRateReport = () => {
    const d = reportData || {};
    const s = d.summary || {};
    return (
      <>
        <div style={S.summaryBar}>
          <div style={S.summaryCard}><div style={S.summaryLabel}>Total Disbursed</div><div style={S.summaryValue}>{formatCurrency(s.totalDisbursed || 0)}</div></div>
          <div style={S.summaryCard}><div style={S.summaryLabel}>Total Payable</div><div style={S.summaryValue}>{formatCurrency(s.totalPayable || s.expectedAmount || 0)}</div></div>
          <div style={S.summaryCard}><div style={S.summaryLabel}>Actually Paid</div><div style={{ ...S.summaryValue, color: '#059669' }}>{formatCurrency(s.actualPaid || s.collectedAmount || 0)}</div></div>
          <div style={S.summaryCard}><div style={S.summaryLabel}>Collection Rate</div><div style={{ ...S.summaryValue, color: s.color || getAchievementColor(s.rate) }}>{s.rateDisplay || '0%'}</div></div>
          <div style={S.summaryCard}><div style={S.summaryLabel}>To Reach 95%</div><div style={{ ...S.summaryValue, color: '#d97706' }}>{formatCurrency(s.amountTo95 || 0)}</div></div>
          <div style={S.summaryCard}><div style={S.summaryLabel}>To Reach 98%</div><div style={{ ...S.summaryValue, color: '#7c3aed' }}>{formatCurrency(s.amountTo98 || 0)}</div></div>
          <div style={S.summaryCard}><div style={S.summaryLabel}>To Reach 100%</div><div style={{ ...S.summaryValue, color: '#2563eb' }}>{formatCurrency(s.amountTo100 || 0)}</div></div>
        </div>
        <div style={S.infoBox}>
          🎯 <strong>Calculation:</strong> Total Payable = loan amount + interest + penalties. Actually Paid = all repayments received for loans disbursed in that month. If a loan is disbursed in one month and repaid in the next, the repayment is credited to the <strong>disbursement month</strong>. The report then shows what is still needed to reach <strong>95%</strong>, <strong>98%</strong> and <strong>100%</strong>.
        </div>
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>{COLLECTION_RATE_COLUMNS.map(c => <th key={c.key} style={c.align === 'right' ? S.thRight : S.th}>{c.label}</th>)}</tr></thead>
            <tbody>{renderRows(d.months || [], COLLECTION_RATE_COLUMNS)}</tbody>
          </table>
        </div>
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER: Report View (when a report is selected)
  // ═══════════════════════════════════════════════════════════════════════
  const renderReportView = () => (
    <div style={S.page}>
      <button
        style={S.backBtn}
        onClick={() => {
          // Cancel any in-progress export
          if (exportAbortRef.current) {
            exportAbortRef.current.abort();
            exportAbortRef.current = null;
          }
          setExporting(false);
          setExportProgress(null);
          setActiveReport(null);
          setReportData(null);
          setReportRows([]);
          setPagination(null);
          setReportSummary(null);
          setError(null);
        }}
      >
        ← Back to Reports
      </button>
      <div style={{ marginBottom: 16 }}>
        <h2 style={S.reportTitle}>{activeReport.name}</h2>
        <p style={S.reportSubtitle}>{activeReport.desc}</p>
      </div>

      {renderFilterBar()}

      {renderExportProgress()}

      {error && <div style={S.errorBox}>⚠ {error}</div>}

      {loading ? (
        <div style={S.loading}>
          <span style={{ fontSize: 20 }}>⏳</span> Loading report data...
        </div>
      ) : (
        <>
          {activeReport.type === 'branch-performance' && renderPerformanceDashboard()}
          {activeReport.type === 'past-maturity-overdue' && renderPastMaturityOverdue()}
          {activeReport.type === 'collection-rate' && renderCollectionRateReport()}
          {!['branch-performance', 'past-maturity-overdue', 'collection-rate'].includes(activeReport.type) && renderStandardTable()}
        </>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════
  return activeReport ? renderReportView() : renderReportList();
}